/**
 * EnumR — single-select enum renderer.
 * Supports inline options and options_ref (async reference-data load).
 * Uses Select for >4 options or when options_ref is set; RadioGroup for ≤4 inline options.
 */

import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Select, RadioGroup } from '../../../components/intake/fields';
import { ComboboxField } from '../../../components/intake/ComboboxField';
import type { FieldDef, RendererProps, RefDataOption } from '../types';
import { loadRefData, getRefDataSync } from '../reference-data/loader';
import { resolveLabel, resolveHelpText, deriveFieldName } from '../utils';
import { buildValidationRules } from '../validation/fromSchema';
import { lookupFieldTooltip } from '../../fieldTooltips';

interface Props extends RendererProps {
  field: FieldDef;
}

export function EnumR({ field, formId, pdfMode = false, namePrefix, audience }: Props) {
  const { getValues } = useFormContext();
  const name = deriveFieldName(field, namePrefix);
  const label = resolveLabel(field, formId, audience);
  const hint = resolveHelpText(field, audience);

  // Resolve options: inline or from reference-data
  const [asyncOptions, setAsyncOptions] = useState<RefDataOption[] | null>(
    field.options_ref ? (getRefDataSync(field.options_ref) ?? null) : null,
  );

  useEffect(() => {
    if (field.options_ref && asyncOptions === null) {
      loadRefData(field.options_ref).then(setAsyncOptions);
    }
  }, [field.options_ref, asyncOptions]);

  const options: Array<{ value: string; label: string }> = field.options
    ? field.options
    : (asyncOptions ?? []);

  if (pdfMode) {
    const value = getValues(name);
    const matched = options.find((o) => o.value === value);
    return (
      <div>
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</span>
        <p className="text-sm text-slate-800 mt-0.5">
          {matched?.label ?? value ?? <span className="text-slate-400 italic">Not provided</span>}
        </p>
      </div>
    );
  }

  // Loading state for async options
  if (field.options_ref && asyncOptions === null) {
    return (
      <div>
        <span className="form-label">{label}</span>
        <div className="form-input animate-pulse bg-slate-100 text-transparent">Loading...</div>
      </div>
    );
  }

  // Use radio buttons for small inline lists (≤4 options, no options_ref)
  const useRadio = !field.options_ref && options.length > 0 && options.length <= 4;

  const tooltip = lookupFieldTooltip(field.canonical_name);
  // RC-4: default-label required message, so the inline error matches the
  // submit-gate summary (validateForm), not the generic fallback.
  const rules = buildValidationRules(field);

  if (useRadio) {
    return (
      <RadioGroup
        name={name}
        label={label}
        hint={hint}
        required={field.required}
        options={options}
        tooltip={tooltip}
        rules={rules}
      />
    );
  }

  // Searchable combobox for the country list or any long enum (>25 options).
  const useCombobox =
    options.length > 25 || (field.options_ref?.includes('countries') ?? false);

  if (useCombobox) {
    return (
      <ComboboxField
        name={name}
        label={label}
        hint={hint}
        required={field.required}
        options={options}
        tooltip={tooltip}
        rules={rules}
      />
    );
  }

  return (
    <Select
      name={name}
      label={label}
      hint={hint}
      required={field.required}
      options={options}
      tooltip={tooltip}
      rules={rules}
    />
  );
}
